'use client';

import {default as Image} from '@/components/global/images/resized-cdn-image';
import { useParams } from 'next/navigation';
import React, { useEffect, useRef, useState } from 'react';

import { doTranslation } from '@/app/i18n/client';

import styles from '@/components/global/navigations/country-language-selector.module.scss';

import ca from '@/public/assets/images/global/flags/ca.svg';
import us from '@/public/assets/images/global/flags/us.svg';
import chevronDarkDown from '@/public/assets/images/global/chevron-dark-down.svg';
import chevronLightDown from '@/public/assets/images/global/chevron-light-down.svg';
import chevronDarkUp from '@/public/assets/images/global/chevron-dark-up.svg';
import chevronLightUp from '@/public/assets/images/global/chevron-light-up.svg';
import dynamic from 'next/dynamic'
const CountryLanguageSelectorPopup = dynamic(() => import('./country-language-selector-popup'))

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  country: string;
  language: string;
}

const CountryLanguageSelector = (props: Props): React.JSX.Element => {
  const { lng }: any = useParams();
  const ref = useRef<HTMLLIElement>(null);
  const [status, setStatus] = useState(false);
  const { t } = doTranslation(lng);

  useEffect(() => {
    const handleClick = (event: any) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setStatus(false);
      }
    };

    const handleTab = (event: any) => {
      if (event.keyCode === 9) {
        if (ref.current && !ref.current.contains(event.target)) {
          setStatus(false);
        }
      }
    };

    document.addEventListener('click', handleClick, true);
    document.addEventListener('keyup', handleTab, true);
    return () => {
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('keyup', handleTab);
    };
  }, []);

  const countries = [
    { image: us, label: t('United States'), value: 'us' },
    { image: ca, label: t('Canada'), value: 'ca' }
  ];
  const languages = [
    { label: t('English'), value: 'en' },
    { label: t('Spanish'), value: 'es' },
    { label: t('French'), value: 'fr' }
  ];


  const c = countries.find((c: any) => c.value === (props.country || 'us'));
  const l = languages.find((l: any) => l.value === (props.language || 'en'));

  return (
    <li className={styles.container} ref={ref}>
      <button
        aria-controls="country-language-selector-options"
        aria-expanded={status}
        aria-haspopup="listbox"
        aria-labelledby="country-language-selector-label"
        className={styles.button}
        onClick={() => setStatus(!status)}
        type="button"
      >
        <span className="sr-only" id="country-language-selector-label">
          {t('Country Language Selector')}
        </span>
        <Image alt={c?.label || ''} height="30" className={styles.flag} src={c?.image} width="30" />
        {l?.label}
        {status ? (
          <Image alt={t('up arrow')} className={styles.large} height="17" src={chevronDarkUp} width="17" />
        ) : (
          <Image alt={t('down arrow')} className={styles.large} height="17" src={chevronDarkDown} width="17" />
        )}
        {status ? (
          <Image alt={t('up arrow')} className={styles.small} height="17" src={chevronLightUp} width="17" />
        ) : (
          <Image alt={t('down arrow')} className={styles.small} height="17" src={chevronLightDown} width="17" />
        )}
      </button>
      {status && <CountryLanguageSelectorPopup country={props.country} countries={countries} language={props.language} languages={languages} />}
    </li>
  );
};

export default CountryLanguageSelector;
